/**
 * Melee swing geometry — the ONE source for the vertical shape of a melee
 * swing (plan 29 D55, 2026-10-11).
 *
 * DJ approved lane BB's flatter swing: a crescent from 25° above facing to
 * 15° below it. D55 makes the hit cone follow that swing instead of the old
 * symmetric wedge (warrior ±60°, paladin ±55°, shaman ±50°), plus a small
 * forgiveness margin, while every class keeps its reach. The renderer draws
 * the bright crescent over `swingUpDeg..swingDownDeg` and a faint wash over the
 * whole real area (`swing + forgivenessDeg`, out to the reach); the server
 * scan and the client prediction test the same wedge — all three go through
 * `resolveMeleeCone` (src/shared/logic/MeleeConeProfile.ts).
 *
 * The margin is measured, not guessed: `scripts/qa/melee-reach-measure.ts`
 * compares the old wedge with the new one over 40×40…80×100 bodies, every
 * horizontal gap from touching to the reach, on flat ground and one platform
 * step (±24/32/48 px) up or down. At 15° nothing that landed before stops
 * landing there (plan 13 §Lane BS has the table); at 10° a 40×40 body one
 * 48 px step below, flush against the attacker, stops landing.
 *
 * Angles are degrees for a RIGHT-facing attacker; negative = above facing
 * (screen Y grows down). A narrower authored arc (rogue 55° = ±27.5°) is
 * never widened: each side is the smaller of the authored half-arc and this
 * shape. A full circle (360°) is a radius and is never flattened.
 */
export const MELEE_SWING_GEOMETRY = {
  /** The drawn crescent's top edge, degrees above facing (lane BB). */
  swingUpDeg: 25,
  /** The drawn crescent's bottom edge, degrees below facing (lane BB). */
  swingDownDeg: 15,
  /** Extra degrees on each side the hit cone allows past the crescent. */
  forgivenessDeg: 15,
} as const;

/** The hit cone's half-angles above/below facing for an authored full arc. */
export function meleeSwingHalfAngles(arcDeg: number): { upDeg: number; downDeg: number } | null {
  const arc = Math.max(0, Math.min(360, arcDeg));
  if (arc >= 360) return null;
  const half = arc / 2;
  const g = MELEE_SWING_GEOMETRY;
  return {
    upDeg: Math.min(half, g.swingUpDeg + g.forgivenessDeg),
    downDeg: Math.min(half, g.swingDownDeg + g.forgivenessDeg),
  };
}
